import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import { scrollToId } from '@/components/site/Navbar';
import { ArrowRight, Broadcast as Radar, Crosshair } from '@phosphor-icons/react';

export default function Hero() {
  const { t } = useLanguage();
  return (
    <section
      id="home"
      data-testid={TID.hero.root}
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center bg-[#0A1128]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Advanced command center radar room"
          className="w-full h-full object-cover object-center scale-105"
        />
      </div>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/85 via-[#0A1128]/70 to-[#0A1128]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/85 via-[#0A1128]/30 to-transparent" />

      {/* Grid lines */}
      <div className="absolute inset-0 bg-grid-lines opacity-50" />

      {/* Radar pulse decoration */}
      <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="relative w-[420px] h-[420px]">
          {[0, 0.6, 1.2, 1.8].map((d) => (
            <span
              key={d}
              className="absolute inset-0 border border-[#4F6355]/40 rounded-full radar-pulse"
              style={{ animationDelay: `${d}s` }}
            />
          ))}
          <span className="absolute inset-[42%] rounded-full bg-[#4F6355]" />
          <span className="absolute inset-1/2 w-px h-1/2 bg-[#4F6355]/40 origin-top rotate-45" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-24 w-full">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-3 px-3 py-2 border border-white/15 bg-white/5 backdrop-blur-sm"
          >
            <Crosshair weight="duotone" size={14} className="text-[#4F6355]" />
            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#F8FAFC]">
              {t.hero.overline}
            </span>
          </motion.div>

          <motion.h1
            data-testid={TID.hero.headline}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-display font-black uppercase tracking-tighter mt-8 text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95]"
          >
            {t.hero.headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 text-lg md:text-xl text-[#94A3B8] leading-relaxed max-w-3xl"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-5 text-sm md:text-base text-[#94A3B8]/80 leading-relaxed max-w-3xl"
          >
            {t.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid={TID.hero.primaryCta}
              onClick={() => scrollToId('contact')}
              className="btn-primary inline-flex items-center justify-center gap-3 px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-bold"
            >
              {t.cta.contactUs}
              <ArrowRight size={16} weight="bold" />
            </button>
            <button
              data-testid={TID.hero.secondaryCta}
              onClick={() => scrollToId('capabilities')}
              className="btn-outline-dark inline-flex items-center justify-center gap-3 px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-bold"
            >
              {t.cta.learnMore}
              <Radar size={16} weight="bold" />
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-16 grid grid-cols-3 max-w-2xl border-t border-white/10"
          >
            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, idx) => (
              <div
                key={idx}
                className={`px-4 sm:px-6 py-6 ${idx !== 0 ? 'border-l border-white/10' : ''}`}
              >
                <div className="font-display font-black text-3xl md:text-4xl text-white tracking-tight">
                  {s.value}
                </div>
                <div className="mt-2 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-[#94A3B8] font-semibold">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom marker */}
      <div className="absolute bottom-6 left-6 md:left-10 z-10 font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]/80">
        {'45.94°N · 24.97°E · RO'}
      </div>
      <div className="absolute bottom-6 right-6 md:right-10 z-10 font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]/80">
        {'Scroll ↓'}
      </div>
    </section>
  );
}
